import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  TablePagination,
  Tooltip,
  IconButton
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import { createDimensionValoracion, deleteDimensionValoracion, getDimensionValoracion, updateDimensionValoracion } from '../../services/dimensionValoracion/dimensionValoracionData';
import { toast } from "react-toastify";

const TablaDV = () => {
  const [datos, setDatos] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [modificarIndex, setModificarIndex] = useState(-1);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const dimensarionValoracionData = await getDimensionValoracion();
        setDatos(Array.isArray(dimensarionValoracionData) ? dimensarionValoracionData : []);
      } catch (error) {
        console.error('Error fetching data:', error);
        setDatos([]);
      }
    };
    fetchData();
  }, []);

  const [formulario, setFormulario] = useState({
    Nombre: '',
    Descripcion: '',
  });

  const handleBusquedaChange = (event) => {
    setBusqueda(event.target.value);
    setPage(0);
  };

  const handleInsert = () => {
    setModificarIndex(-1);
    setFormulario({
      Nombre: '',
      Descripcion: '',
    });
    setModalOpen(true);
  };

  const handleFormChange = (event) => {
    setFormulario({
      ...formulario,
      [event.target.name]: event.target.value,
    });
  };

  const handleGuardar = async () => {
    if (!formulario.Nombre || formulario.Nombre.trim() === "") {
      toast.warning("El nombre es obligatorio");
      return;
    }

    try {
      if (modificarIndex !== -1) {
        await updateDimensionValoracion(modificarIndex, formulario);
        const dimensarionValoracionData = await getDimensionValoracion();
        setDatos(Array.isArray(dimensarionValoracionData) ? dimensarionValoracionData : []);
        toast.success("Dimensión modificada correctamente");
      } else {
        await createDimensionValoracion(formulario);
        const dimensarionValoracionData = await getDimensionValoracion();
        setDatos(Array.isArray(dimensarionValoracionData) ? dimensarionValoracionData : []);
        toast.success("Dimensión creada correctamente");
      }
      setModalOpen(false);
      setFormulario({
        Nombre: '',
        Descripcion: '',
      });
    } catch (error) {
      console.error("Error saving dimension:", error);
      toast.error("Error al guardar la dimensión");
    }
  };

  const handleCancelar = () => {
    setModalOpen(false);
    setFormulario({
      Nombre: '',
      Descripcion: '',
    });
  };

  const handleModificar = (index) => {
    setModificarIndex(datos[index].DIV_CODIGO);
    setFormulario({
      Nombre: datos[index].DIV_NOMBRE,
      Descripcion: datos[index].DIV_DESCRIPCION,
    });
    setModalOpen(true);
  };

  const handleEliminar = (index) => {
    setDeleteIndex(index);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    const codigoToDelete = Array.isArray(datos) ? datos[deleteIndex]?.DIV_CODIGO : undefined;
    if (!codigoToDelete) {
      toast.error("No se encontró la dimensión a eliminar.");
      setConfirmOpen(false);
      setDeleteIndex(null);
      return;
    }
    try {
      await deleteDimensionValoracion(codigoToDelete);
      toast.success("Dimensión eliminada correctamente");
      const dimensarionValoracionData = await getDimensionValoracion();
      setDatos(Array.isArray(dimensarionValoracionData) ? dimensarionValoracionData : []);
    } catch (error) {
      console.error("Error deleting dimension:", error);
      toast.error("Error al eliminar la dimensión");
    } finally {
      setConfirmOpen(false);
      setDeleteIndex(null);
    }
  };

  const handleCancelDelete = () => {
    setConfirmOpen(false);
    setDeleteIndex(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const datosFiltrados = Array.isArray(datos)
    ? datos.filter(
      (dato) =>
        (dato.DIV_NOMBRE && dato.DIV_NOMBRE.toLowerCase().includes(busqueda.toLowerCase())) ||
        (dato.DIV_DESCRIPCION && dato.DIV_DESCRIPCION.toLowerCase().includes(busqueda.toLowerCase()))
    )
    : [];

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <TextField
          variant="outlined"
          size="small"
          placeholder="Buscar dimensión..."
          value={busqueda}
          onChange={handleBusquedaChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ width: 300 }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleInsert}
          sx={{
            backgroundColor: '#3A6B7E',
            '&:hover': { backgroundColor: '#2C5261' }
          }}
        >
          Nueva Dimensión
        </Button>
      </Box>

      <TableContainer>
        <Table sx={{ minWidth: 750 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#001F3F' }}>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Código</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Nombre</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Descripción</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }} align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {datosFiltrados.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                  <Typography variant="body1" color="textSecondary">
                    {datos.length === 0 ? "No hay dimensiones registradas." : "No se encontraron resultados."}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              datosFiltrados
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((dato, index) => (
                  <TableRow key={dato.DIV_CODIGO} hover>
                    <TableCell>{dato.DIV_CODIGO}</TableCell>
                    <TableCell>{dato.DIV_NOMBRE}</TableCell>
                    <TableCell>{dato.DIV_DESCRIPCION}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Editar">
                        <IconButton onClick={() => handleModificar(index + (page * rowsPerPage))}>
                          <EditIcon sx={{ color: "#3A6B7E" }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton onClick={() => handleEliminar(index + (page * rowsPerPage))}>
                          <DeleteIcon sx={{ color: "#d32f2f" }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={datosFiltrados.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Filas por página:"
      />

      {/* Modal Formulario */}
      <Dialog
        open={modalOpen}
        onClose={handleCancelar}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ backgroundColor: '#001F3F', color: '#fff', pb: 2 }}>
          {modificarIndex !== -1 ? 'Modificar Dimensión' : 'Nueva Dimensión'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Nombre"
              name="Nombre"
              fullWidth
              value={formulario.Nombre}
              onChange={handleFormChange}
              variant="outlined"
              error={!formulario.Nombre && modalOpen}
              helperText={!formulario.Nombre && modalOpen ? "Campo obligatorio" : ""}
            />
            <TextField
              label="Descripción"
              name="Descripcion"
              fullWidth
              value={formulario.Descripcion}
              onChange={handleFormChange}
              variant="outlined"
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCancelar} sx={{ color: '#666' }}>Cancelar</Button>
          <Button
            onClick={handleGuardar}
            variant="contained"
            sx={{
              backgroundColor: '#001F3F',
              '&:hover': { backgroundColor: '#003366' }
            }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Confirmación Eliminación */}
      <Dialog
        open={confirmOpen}
        onClose={handleCancelDelete}
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ backgroundColor: '#d32f2f', color: '#fff' }}>
          Confirmar Eliminación
        </DialogTitle>
        <DialogContent sx={{ pt: 3, mt: 2 }}>
          <Typography>
            ¿Está seguro que desea eliminar esta dimensión de valoración?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCancelDelete} sx={{ color: '#666' }}>Cancelar</Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TablaDV;
